import { motion } from 'framer-motion'
import { observer } from 'mobx-react'
import React, { useId, useState } from 'react'

import {
   Button,
   colors,
   Hierarchy,
   SubVariant
} from '@flowwai-frontend/design-system'
import {
   AlertTriangleFeaturedIcon,
   XCloseIcon
} from '@flowwai-frontend/design-system/icons'
import { ConfirmationModal } from '@flowwai-frontend/shared/common-components'
import { isMobile } from '@flowwai-frontend/shared/utils/web'

import { useMapsUtilityContext } from '../../../hooks/useMapsUtilityContext'
import MapTooltip from '../../MapTooltip/MapTooltip'
import * as Styles from './styles'

interface Props {
   isGeoShapeChanged: boolean
   errorMessage: string | null | undefined
   onSaveLocation: () => void
   onCloseModal: () => void
}

const CloseMapsTool = (props: Props): React.ReactElement => {
   const {
      isGeoShapeChanged,
      errorMessage,
      onSaveLocation,
      onCloseModal
   } = props

   const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)

   const isMobileDevice = isMobile()

   if (isMobileDevice) return <></>

   const onCloseConfirmationModal = () => {
      setIsConfirmationModalOpen(false)
   }

   const handleOnClose = (): void => {
      if (isGeoShapeChanged) setIsConfirmationModalOpen(true)
      else onCloseModal()
   }

   const renderBackButton = (): React.ReactElement => (
      <motion.div
         className={'absolute top-6 right-6'}
         {...Styles.closeIconAnimationProps}
      >
         <div className={Styles.closeIconButtonStyles} onClick={handleOnClose}>
            <XCloseIcon width={20} height={20} fill={colors['gray-700']} />
         </div>
      </motion.div>
   )

   const renderConfirmationCard = () => (
      <ConfirmationCard
         onCancel={onCloseConfirmationModal}
         onConfirm={onSaveLocation}
         onCloseModal={onCloseModal}
         errorMessage={errorMessage}
      />
   )

   const renderConfirmationModal = () => (
      <ConfirmationModal
         onCloseModal={onCloseConfirmationModal}
         isOpen={isConfirmationModalOpen}
         renderConfirmationCard={renderConfirmationCard}
         apiStatus={200}
      />
   )

   return (
      <React.Fragment>
         {renderBackButton()}

         {renderConfirmationModal()}
      </React.Fragment>
   )
}

const ConfirmationCard = ({
   errorMessage,
   onConfirm,
   onCancel,
   onCloseModal
}: {
   errorMessage: string | null | undefined
   onConfirm: () => void
   onCancel: () => void
   onCloseModal: () => void
}) => {
   const { defaultViewMode, mapsT } = useMapsUtilityContext()

   const tooltipId = useId()

   const isDefaultEditMode = defaultViewMode === 'EDIT'

   const renderSaveChangesButton = (): React.ReactElement => (
      <React.Fragment>
         <Button
            size={'Large'}
            hierarchy={
               isDefaultEditMode ? Hierarchy.Secondary : Hierarchy.Tertiary
            }
            subVariant={SubVariant.Primary}
            onClick={onConfirm}
            isDisabled={!!errorMessage}
            data-tooltip-id={tooltipId}
         >
            {mapsT('saveChangesConfirmation.saveChanges')}
         </Button>
         {errorMessage ? (
            <MapTooltip content={errorMessage} tooltipId={tooltipId} />
         ) : null}
      </React.Fragment>
   )

   const renderDiscardChangesButton = (): React.ReactElement => (
      <Button
         size={'Large'}
         hierarchy={
            isDefaultEditMode ? Hierarchy.Tertiary : Hierarchy.Secondary
         }
         subVariant={SubVariant.Destructive}
         onClick={onCloseModal}
      >
         {mapsT('saveChangesConfirmation.discardChanges')}
      </Button>
   )

   return (
      <div className='w-[560px] p-6 bg-white rounded-2xl flex flex-col'>
         <AlertTriangleFeaturedIcon />

         <div className='flex flex-col gap-1 mt-4 mb-8'>
            <div className='text-gray-900 text-lg-semibold'>
               {mapsT('saveChangesConfirmation.title')}
            </div>
            <div className='text-slate-600 text-sm-normal'>
               {mapsT('saveChangesConfirmation.description')}
            </div>
         </div>

         <div className='flex justify-between items-center'>
            <Button
               size={'Large'}
               hierarchy={Hierarchy.Tertiary}
               subVariant={SubVariant.Gray}
               onClick={onCancel}
            >
               {mapsT('saveChangesConfirmation.cancel')}
            </Button>

            <div className='flex items-center gap-3'>
               {isDefaultEditMode ? (
                  <>
                     {renderDiscardChangesButton()}
                     {renderSaveChangesButton()}
                  </>
               ) : (
                  <>
                     {renderSaveChangesButton()}
                     {renderDiscardChangesButton()}
                  </>
               )}
            </div>
         </div>
      </div>
   )
}

export default observer(CloseMapsTool)
